import React, { MouseEvent, useMemo } from 'react'
import { useReactiveVar } from '@apollo/client'
import { useNavigate } from 'react-router-dom';
import { Loading } from 'components/Loading';
import { FilterCheck, FilterInput } from 'components/UIParts';
import { filterStates } from 'database/AppApolloProvider';
import options from 'contexts/options.json';
import { useMongoQueries } from 'contexts/useMongoQuery';
import { DataTable, TableHeadBase, createColumnsFromHeads } from 'components/DataTable';

const createHeads = <T extends readonly TableHeadBase[]>(configs: T): T => configs

const heads = createHeads([
    {
        field: "internal",
        text: "院内",
        cell: (row: KV) => (row.internal ? "◯" : ""),
    },
    {
        field: "notice",
        text: "通知メール",
        cell: (row: KV) => (row.notice ? "◯" : ""),
    },
    { field: "clinic.clinic_name", text: "施設名" },
    { field: "name", text: "氏名" },
    { field: "role", text: "役割" },
    { field: "status", text: "ステータス", type: "Option", optionKey: "userStatus" },
])
const getQueryVariables = (names: KV): KV[] => {
    let matchConditions: KV = {}

    if (names.name) {
        matchConditions.name = { $regex: names.name, $options: 'i' }
    }

    if (!names.internal && !names.external) {
        matchConditions.internal = { $exists: false }
    } else if (names.internal && !names.external) {
        matchConditions.internal = true
    } else if (!names.internal && names.external) {
        matchConditions.internal = false
    }

    const pipeline: KV[] = [
        { $match: matchConditions },
        {
            $lookup: {
                from: "clinics",
                localField: "clinic",
                foreignField: "_id",
                as: "clinicDetails"
            }
        },
        {
            $unwind: {
                path: "$clinicDetails",
                preserveNullAndEmptyArrays: true
            }
        },
        {
            $addFields: {
                "clinic.clinic_name": "$clinicDetails.clinic_name"
            }
        },
        ...(names.clinic && names.clinic.clinic_name ? [{
            $match: {
                "clinicDetails.clinic_name": { $regex: names.clinic.clinic_name, $options: 'i' }
            }
        }] : []),
        {
            $project: {
                _id: 1,
                internal: 1,
                notice: 1,
                name: 1,
                role: 1,
                status: 1,
                clinic: {
                    _id: "$clinic",
                    clinic_name: "$clinicDetails.clinic_name"
                }
            }
        }
    ]
    return pipeline
}

export const List = () => {
    const navigate = useNavigate()
    const values: KV = useReactiveVar(filterStates)
    const names = values.user || { clinic: { clinic_name: "" }, name: "", internal: true, external: true }
    const { loading, data } = useMongoQueries("user", getQueryVariables(names), { limit: 200 })
    const rows: KV[] = data || []
    const columns = useMemo(() => createColumnsFromHeads(heads), [])
    const setValue = (name: string, value: any) => {
        switch (name) {
            case "clinic.clinic_name":
                filterStates({ ...filterStates(), user: { ...names, clinic: { clinic_name: value } } })
                break
            default:
                filterStates({ ...filterStates(), user: { ...names, [name]: value } })
                break
        }

    }
    const handleClick = (e: MouseEvent<HTMLTableRowElement>) => {
        e.preventDefault()
        e.stopPropagation()
        navigate(`/user/edit/${e.currentTarget.dataset.id}`)
    }

    if (loading) {
        return <div className="w-full h-full flex justify-center items-center"><Loading /></div>
    }

    const filterContent = (
        <>
            <FilterCheck label="院内" name="internal" value={names.internal} setValue={setValue} />
            <FilterCheck label="外部" name="external" value={names.external} setValue={setValue} />
            <FilterInput label="施設名" name="clinic.clinic_name" value={names.clinic.clinic_name} setValue={setValue} />
            <FilterInput label="氏名" name="name" value={names.name} setValue={setValue} />
        </>
    )

    return (
        <DataTable<KV>
            data={rows}
            columns={columns}
            filterContent={filterContent}
            itemCount={rows.length}
            onAdd={() => navigate('/user/create')}
            getRowProps={row => ({
                key: (row.original as KV)._id,
                'data-id': (row.original as KV)._id,
                className: 'text-sm text-gray-900 hover:bg-theme-50 cursor-pointer',
                onClick: handleClick,
            })}
            storageKey="userList_columnSettings"
        />
    )
}

export default List